import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { CoverageAreaConeKonva } from "@project/floorplanner/konva/coverage-area-cone-konva";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { IconKonva } from "@project/floorplanner/konva/icon-konva";
import Konva from "konva";
import { Group } from "konva/lib/Group";
import { Wedge } from "konva/lib/shapes/Wedge";

export interface FlameDetectorKonvaShapes {
  flameDetectorGroup: Konva.Group;
  flameDetector: Konva.Image;
  coverageAreaCone?: Wedge;
}

export class FlameDetectorKonva {
  private static readonly CONE_ATTR = "coverageAreaCone";

  static init(
    flameDetector: FloorplanFlameDetector | FloorplanFlameDetectorPlaceholder,
    flameDetectorIcon: Konva.Image,
    warningSignIcon: Konva.Image,
    fieldOfViewDeg: number,
    productGroup?: Konva.Group,
  ): FlameDetectorKonvaShapes {
    const isPlaceholder = flameDetector instanceof FloorplanFlameDetectorPlaceholder;
    const group = new Konva.Group({
      id: flameDetector.id,
      [FloorplanWorkspaceKonvaAdapter.OBJECT_KEY]: flameDetector,
    });

    let coverageAreaCone: Wedge | undefined;
    if (!isPlaceholder) {
      coverageAreaCone = CoverageAreaConeKonva.init(flameDetector as FloorplanFlameDetector, fieldOfViewDeg);
      group.setAttr(FlameDetectorKonva.CONE_ATTR, coverageAreaCone);
    }

    const iconGroup = IconKonva.init(
      flameDetector,
      flameDetectorIcon,
      warningSignIcon,
      productGroup,
      (icon: Konva.Image) => {
        if (coverageAreaCone) {
          CoverageAreaConeKonva.setPosition(coverageAreaCone, icon.x(), icon.y(), icon.width() * icon.scaleX());
        }
      },
      (icon: Konva.Image) => {
        if (coverageAreaCone) {
          CoverageAreaConeKonva.setPosition(coverageAreaCone, icon.x(), icon.y(), icon.width() * icon.scaleX());
          CoverageAreaConeKonva.setStrokeWidth(coverageAreaCone, icon.scaleX());
        }
      },
    );
    if (coverageAreaCone) {
      CoverageAreaConeKonva.setPosition(
        coverageAreaCone,
        flameDetectorIcon.x(),
        flameDetectorIcon.y(),
        flameDetectorIcon.width() * flameDetectorIcon.scaleX(),
      );
      CoverageAreaConeKonva.applyInitialScale(coverageAreaCone, flameDetectorIcon.scaleX());
    }
    group.add(iconGroup);

    return {
      flameDetectorGroup: group,
      flameDetector: flameDetectorIcon,
      coverageAreaCone,
    };
  }

  static refreshItemText(flameDetector: FloorplanFlameDetector | FloorplanFlameDetectorPlaceholder, group: Group): void {
    const iconGroup = group.getChildren()[0] as Group;
    IconKonva.refreshItemText(flameDetector, iconGroup);
  }

  static setCoverageAreaVisibility(group: Konva.Group, visible: boolean): void {
    const cone = this.getCoverageAreaCone(group);
    if (cone) {
      cone.visible(visible);
    }
  }

  static setCoverageAreaRadius(group: Konva.Group, radius: number): void {
    const cone = this.getCoverageAreaCone(group);
    if (cone) {
      cone.radius(radius);
    }
  }

  static setConeRotation(group: Konva.Group, rotationDeg: number, fieldOfViewDeg: number): void {
    const cone = this.getCoverageAreaCone(group);
    if (cone) {
      CoverageAreaConeKonva.setConeRotation(cone, rotationDeg, fieldOfViewDeg);
    }
  }

  static refreshWarningSignVisibility(group: Group, visible: boolean): void {
    IconKonva.refreshWarningSignVisibility(this.getIconGroup(group), visible);
  }

  static getCoverageAreaCone(group: Konva.Group): Wedge | undefined {
    return group.getAttr(FlameDetectorKonva.CONE_ATTR) as Wedge | undefined;
  }

  private static getIconGroup(group: Konva.Group): Konva.Group {
    return group.getChildren()[0] as Konva.Group;
  }
}
